import { ErrorMessage } from '@/components/loading-error';
import { AnimatePresence } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { ControlPanel } from '../../components/ui/controlpanel';
import { Header } from '../../components/ui/header';
import { LoadingOverlay } from '../../components/ui/loading';
import {
  RolePlayArea,
  ScenarioInfo,
  SessionPhase,
} from '../../components/ui/scenarioinfo';
import { log, useRolePlay } from '../../hooks/useRolePlay';
import api from '../../utils/ky';
import { useCoachStore } from '@/store/coach';
import ConnectionMonitor from '../../components/connection-monitor';
import { usePostHog } from 'posthog-js/react';
import { useTranslation } from 'react-i18next';
import { constructLanguageUrl } from '../../router';

// Constants
const COACH_API_ENDPOINTS = {
  getSession: (scenario: string, language?: string) =>
    `api/coach/sessions/${scenario}${language ? `?language=${language}` : ''}`,
  endSession: 'api/coach/roleplay/end-session',
};

export const CoachRolePlay = () => {
  const navigate = useNavigate();
  const coachStore = useCoachStore();
  const posthog = usePostHog();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId');
  const code = searchParams.get('code');
  const { t } = useTranslation();
  const { lang } = useParams();

  // State
  const [loadingState, setLoadingState] = useState('initial');
  const [sessionPhase, setSessionPhase] = useState<SessionPhase>(
    SessionPhase.PRE_START,
  );
  const [isAISpeaking, setIsAISpeaking] = useState(false);
  const [isIntroCompleted, setIsIntroCompleted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startTimeRef = useRef<number>(0);

  // Session data
  const rolePlaySessionRef = useRef<any>(null);

  const fetchSession = useCallback(async () => {
    log('Fetching session:', sessionId);
    try {
      setLoadingState('loading');
      const data: any = await api
        .get(COACH_API_ENDPOINTS.getSession(sessionId!, lang))
        .json();
      log('Fetched session:', data);

      rolePlaySessionRef.current = data;
      setLoadingState('ready');

      posthog.capture('coach_roleplay_loaded', {
        code: searchParams.get('code'),
        userId: coachStore.userId,
        sessionId: data.sessionId,
      });
    } catch (error) {
      log('Error fetching session:', error);
      setLoadingState('error');
      toast.error('Failed to fetch session. Please try again.');
    }
  }, [sessionId]);

  useEffect(() => {
    log('useEffect Scenario:', sessionId);
    if (sessionId) {
      fetchSession();
    }
  }, [sessionId, fetchSession]);

  const checkMediaDevices = async () => {
    try {
      // Check if browser supports getUserMedia
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        alert('Your browser does not support audio input');
      }

      // Check if any audio input devices exist
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasAudioInput = devices.some(
        (device) => device.kind === 'audioinput',
      );
      if (!hasAudioInput) {
        alert('No audio input devices detected');
      }

      // Test microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Stop the test stream immediately
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (error) {
      if (error.name === 'NotAllowedError') {
        alert(
          'Microphone permission denied. Please allow microphone access to use this feature',
        );
      } else if (error.name === 'NotFoundError') {
        alert(
          'No microphone found. Please connect a microphone and try again',
        );
      }
      throw error;
    }
  };

  const handleStartPractice = async () => {
    try {
      // First check media devices
      await checkMediaDevices();

      await rolePlay.connectConversation();

      posthog.capture('coach_roleplay_started', {
        code: searchParams.get('code'),
        userId: coachStore.userId,
        sessionId: rolePlaySessionRef.current?.sessionId,
        scenario: rolePlaySessionRef.current?.scenario.title,
      });
    } catch (error) {
      log('Error starting practice:', error);
    }
  };

  const handleEndPractice = useCallback(() => {
    log('handleEndPractice');
    rolePlay.disconnectConversation();
  }, []);

  const endSession = async ({ transcript, duration }) => {
    try {
      setLoadingState('ending');

      posthog.capture('coach_roleplay_ended', {
        code: searchParams.get('code'),
        userId: coachStore.userId,
        sessionId: rolePlaySessionRef.current?.sessionId,
        scenario: rolePlaySessionRef.current?.scenario.title,
      });

      await api
        .post(COACH_API_ENDPOINTS.endSession, {
          timeout: 90000,
          retry: {
            limit: 2,
            methods: ['post'],
          },
          json: {
            sessionId,
            transcript,
            duration: (Date.now() - startTimeRef.current) / 1000,
            language: lang,
          },
        })
        .json();

      // Update store with feedback
      coachStore.setLastSessionId(sessionId);
      // coachStore.setLastFeedback(result);
      coachStore.setSessionTranscript(transcript);
      setLoadingState(SessionPhase.DONE);

      // Navigate to new roleplay feedback
      navigate(
        constructLanguageUrl(
          lang,
          '/coach/roleplay-feedback',
          '?' +
            new URLSearchParams({
              code,
              sessionId,
            }).toString(),
        ),
      );
    } catch (error) {
      log('Error ending session:', error);
      toast.error('Failed to end session. Please try again.');
    }
  };

  const rolePlay = useRolePlay({
    sessionId,
    rolePlaySessionRef,
    startTimeRef,
    setSessionPhase,
    endSession,
    setError,
    setIsAISpeaking,
    setIsIntroCompleted,
  });

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      {error && <ErrorMessage message={error} onRetry={rolePlay.handleRetry} />}
      <Header
        scenario={rolePlaySessionRef.current?.scenario}
        startTime={startTimeRef.current}
      />

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Scenario Info & Framework */}
        <div className="w-1/3 bg-white border-r border-gray-200 overflow-y-auto">
          <ScenarioInfo
            scenario={rolePlaySessionRef.current?.scenario}
            framework={rolePlaySessionRef.current?.framework}
            phase={sessionPhase}
            onStart={handleStartPractice}
            className="px-6 py-4"
          />
        </div>

        {/* Main Area - Role Play Interface */}
        <div className="flex-1 flex flex-col relative">
          <RolePlayArea
            isAISpeaking={isAISpeaking}
            isMuted={false}
            scenario={rolePlaySessionRef.current?.scenario}
            volume={100}
          />

          <ConnectionMonitor className="flex items-center justify-center p-2 text-sm mb-24" />

          <ControlPanel
            phase={sessionPhase}
            isAISpeaking={isAISpeaking}
            onEnd={handleEndPractice}
            onSkipIntro={rolePlay.onSkipIntro}
          />
        </div>
      </div>

      {/* Loading States */}
      <AnimatePresence>
        {loadingState !== 'ready' && <LoadingOverlay state={loadingState} />}
      </AnimatePresence>
    </div>
  );
};
